/** User-facing strings: agents. */
export const agents = {
    page: {
        title: "Agents",
        description:
            "The agents of {name}. An agent is a small program that runs in your plant, next to your OPC UA servers, and makes them reachable from anywhere without opening your network to the Internet.",
        serviceStarting: "The service is starting. Retry in a few seconds.",
        retry: "Retry",
        emptyTitle: "No agents yet",
        emptyBody:
            "An agent is a small program that runs in your plant, next to your OPC UA server. It connects the server to your account, so your clients can reach it from anywhere. Install one on a computer in your plant and it appears here, with its status (online or offline) and the last time it was seen.",
        downloadAgent: "Download the agent",
        getToken: "Get the token that links it to your account",
        gdsTip: "Setting up a plant for your GDS?",
        gdsLink: "Go to Your GDS",
        gdsTipAfter: ": an owner or admin connects a plant there with one command, and the agent is installed for you.",
    },
    list: {
        searchRegion: "Search and filter agents",
        searchLabel: "Search agents",
        searchHelp: "Agent name, agent ID (start), server name or URL.",
        sortLabel: "Sort by",
        filterLegend: "Filter by status",
        clear: "Clear search and filters",
        heading: "Agents {from} to {to} of {total}",
        headingPlain: "Agents",
        checkingStatus: "Checking the status of {count, plural, one {# agent} other {# agents}}...",
        noMatchHint: "Try another search, or show all statuses.",
        pagination: "Agents pagination",
        previous: "Previous",
        next: "Next",
        page: "Page {page} of {pageCount}",
        filter: {
            all: "All",
            online: "Online",
            offline: "Offline",
            disabled: "Disabled",
            unavailable: "Unavailable",
        },
        sort: {
            name: "Name (A to Z)",
            status: "Status (online first)",
            lastSeen: "Last seen (most recent first)",
        },
        result: {
            none: "No agents.",
            noMatch: "No agent matches.",
            shown: "{count, plural, one {# agent} other {# agents}} shown.",
            showing: "Showing {from} to {to} of {count, plural, one {# agent} other {# agents}}.",
            inOrganisation: "{total} in the organisation.",
            checking: "Checking the status of {count, plural, one {# agent} other {# agents}}.",
        },
    },
    status: {
        online: "Online ({count})",
        offline: "Offline",
        disabled: "Disabled",
        unavailable: "Status unavailable",
        checking: "checking...",
        neverConnected: "never connected",
        lastConnected: "last connected {when}",
        lastSeen: "last seen {when}",
    },
    card: {
        details: "Details",
        detailsOf: "of agent {name}",
        agentIdLabel: "Agent ID",
        certificateLabel: "Certificate",
        certificateResetOn:
            "Certificate reset on {when}. Link the agent to your account again on the computer it runs on (opcua-relay-agent config <token> -f)",
        certificatePinned: "Certificate fingerprint {fingerprint}…, recorded {when}",
        certificateNone: "No certificate registered yet",
        resetButton: "Reset certificate",
        resetTitle: 'Reset the certificate of agent "{name}"?',
        resetDescription: "This agent is turned away until you link it to your account again, on the computer it runs on.",
        resetDone: 'Certificate of agent "{name}" reset',
        accessLabel: "Access",
        enabled: "Enabled",
        disabledOn: "Disabled on {when}",
        enableButton: "Enable agent",
        enableTitle: 'Enable agent "{name}"?',
        enableDescription:
            "Its previous credential stays revoked: the agent can connect again only after you link it to your account again, on the computer it runs on.",
        enableDone: 'Agent "{name}" enabled: link it to your account again on its computer',
        disableButton: "Disable agent",
        disableTitle: 'Disable agent "{name}"?',
        disableDescription:
            "This agent is turned away and its open connections are closed within about 10 seconds. To enable it again later, link it to your account again on the computer it runs on.",
        disableDone: 'Agent "{name}" disabled',
        lastConnectedLabel: "Last connected",
        lastSeenLabel: "Last seen",
        neverConnected: "Never connected",
        unknown: "Unknown",
        nowConnected: "Now (connected)",
        notIn7Days: "Not in the last 7 days",
        connectionsLabel: "Connections, last 24 hours",
        deleteButton: "Delete agent",
        deleteTitle: 'Delete agent "{name}" permanently?',
        deleteConsequence:
            "This is permanent and cannot be undone. The servers shared through this agent ({count}) are removed, so clients can no longer reach them. To come back, the agent must be linked to your account again.",
        deleteDone: 'Agent "{name}" deleted',
        noServers: "This agent does not connect any OPC UA server yet.",
        serversHeading: "OPC UA servers this agent connects ({count})",
        gatewayNote: "This agent is the gateway of your GDS. Its devices and its connection are on the Your GDS page.",
        gatewayLink: "Open Your GDS",
        advancedToggle: "Advanced",
        advancedToggleOf: "settings of agent {name}",
        advancedHint: "For operators and support. Most agents never need this.",
        advancedGateway: "This agent was set up from Your GDS. Change its devices there, not here.",
    },
    server: {
        badgesLabel: "Server badges",
        upstream: "In your plant:",
        publicUrlLabel: "Address for your clients",
        copyUrlLabel: "Copy the client address of {url}",
        browse: "Browse",
        browseOf: "server {label}",
        actionsFor: "Actions for server {label}",
        testConnection: "Test connection",
        copyUrl: "Copy address",
        deleteServer: "Remove server",
        deleteTitle: "Remove this server from the agent?",
        deleteDescription: "Clients can no longer reach this server at its client address. This cannot be undone.",
        deleted: "Server removed",
        urlCopied: "Client address copied",
        copyFailed: "Could not copy. Select the address and copy it manually.",
    },
    badges: {
        testing: "Testing...",
        reachable: "Reachable",
        otherUrl: "Server gives another address",
        needsConfiguration: "Needs configuration",
        notVerified: "Not verified",
        testFailed: "Test failed",
    },
    probe: {
        tooManyAttempts: "Too many attempts - try again shortly.",
        httpFailed: "Test failed (HTTP {status}).",
        unreachable: "Could not reach the dashboard server to run the test.",
        testing: "Testing the connection...",
        reachable: "Reachable - the server gives clients this address.",
        lastTest: "Last test: {when}",
    },
    detail: {
        sectionLabel: "Setup and test of {label}",
        stacksLabel: "Client stacks for {label}",
        testing: "Testing...",
        testAgain: "Test again",
        testHint:
            "Opens a short test connection without security (discovery only) and checks that your server gives clients the address above.",
        copyUrlOf: "of {label}",
        toggle: "Setup and test",
        toggleOf: "of {label}",
    },
    guide: {
        intro: "A client that connects through this address only opens a session if your server's own",
        introAfter:
            "response lists this exact address. Changing only the host name is not enough: the port and the end of the address also differ from your server's own address in the plant.",
        stacks: "Client stacks",
        snippet: "{label} snippet",
        fullGuide: "Full guide: connecting clients to your servers",
    },
    confirm: {
        cancel: "Cancel",
        commandLabel: "Command to link the agent again",
        runOnHost: "Run it on the computer the agent runs on. Replace",
        withToken: "with your authentication token, shown on the",
        tokenPage: "Token page",
        end: ".",
    },
    timeline: {
        unavailable: "Connection history unavailable.",
        loading: "Loading history...",
        hoursAgo: "24 h ago",
        now: "now",
        summary: "{text}.",
        none: "No connect or disconnect in the last 24 hours.",
        connected: "Connected",
        disconnected: "Disconnected",
        segmentOnline: "online from {when}",
        segmentOffline: "offline from {when}",
        segmentUnknown: "unknown from {when}",
        noData: "No connection data for the last 24 hours",
        online: "Online {percent} of the last 24 hours, {count, plural, one {# disconnect} other {# disconnects}}",
    },
    bindings: {
        title: "Import bindings",
        add: "Add binding",
        adding: "Adding...",
        cancel: "Cancel",
        emptyExplain:
            "Most plants never need this: Connect a plant, on the Your GDS page, creates what a GDS needs. An import binding names the host and port inside our cloud that the relay dials when this agent imports a server into your plant, for example a GDS reverse-connect listener or a GDS server.",
        emptyStepBefore: "Add a binding, copy its id, then run",
        stepAfter: "on the agent.",
        created: 'Binding "{name}" created.',
        createdStepBefore: "Copy the id, then run",
        copy: "Copy",
        copyTheId: "Copy the id",
        copied: "Copied",
        copyIdLabel: "Copy binding id {id}",
        copiedStatus: "Binding id {id} copied to the clipboard.",
        copyFailed: "Could not copy. Select the id and copy it by hand.",
        fieldName: "Name",
        fieldKind: "Kind",
        fieldHost: "Target host",
        fieldPort: "Target port (1 to 65535)",
        kindReverseAdapter: "Cloud client reaches a plant server",
        kindListener: "Plant clients reach a cloud server",
        kindTcp: "Plant browsers reach a cloud web console (raw TCP)",
        errorNameRequired: "Enter a name.",
        errorNameTooLong: "Use at most 64 characters.",
        errorKindRequired: "Choose a kind.",
        errorHostRequired: "Enter a host name or an IP address.",
        errorHostInvalid: "Enter a host name or an IP address, without a scheme or a path.",
        errorPortRequired: "Enter a port.",
        errorPortInvalid: "Enter a whole number from 1 to 65535.",
        noBinding: "The server answered without the new binding.",
        loadError: "Could not load the import bindings: {message}",
        loading: "Loading import bindings...",
        caption: "Import bindings of this agent",
        colName: "Name",
        colKind: "Kind",
        colTarget: "Target",
        colCreated: "Created",
        colBindingId: "Binding id",
        colActions: "Actions",
        deleteLabel: "Delete binding {name}",
        deleteTitle: "Delete binding",
        deleteDialogTitle: 'Delete binding "{name}"?',
        deleteDescription:
            "The binding id stops working: an import that uses it can no longer reach its target. This cannot be undone.",
        deleteConfirm: "Delete binding",
        deleted: "Import binding deleted",
    },
    stats: {
        opened: "opened {count}",
        open: "open {count}",
        lastClose: "last close {reason}",
        lastCloseAt: "last close {reason} {when}",
        limitReached: "limit reached {count}",
        targetUnavailable: "target unavailable {count}",
        unavailable: "stream counters unavailable",
        reading: "reading stream counters",
        // the GDS page, for members: no relay words
        plainNone: "No connection so far",
        plainOpened: "{count, plural, one {# connection so far} other {# connections so far}}",
        plainOpen: "{count} open now",
        plainLast: "the last one ended {when}",
        plainLimit: "{count} turned away because the limit was reached",
        plainUnreachable: "{count, plural, one {# time the GDS could not be reached} other {# times the GDS could not be reached}}",
        plainUnavailable: "Connection counters unavailable",
        plainReading: "Reading the connection counters",
    },
    actions: {
        failed: "Failed to {what}.\n{message}",
        unreachable: "Could not reach the dashboard server. Check your connection and try again.",
        resetCertificate: "reset the certificate",
        disableAgent: "disable the agent",
        enableAgent: "enable the agent",
        deleteAgent: "delete the agent",
        deleteServer: "remove the server",
        addImportBinding: "add the import binding",
        deleteImportBinding: "delete the import binding",
    },
};
